/* eslint-disable jsx-a11y/aria-role -- Should add these for accessibility but don't want to change this file to much */

/* eslint-disable jsx-a11y/no-noninteractive-element-interactions -- Should add these for accessibility but don't want to change this file to much */
/* eslint-disable jsx-a11y/mouse-events-have-key-events -- Should add these for accessibility but don't want to change this file to much */

import { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import { v4 } from 'uuid';

import type { CSSProperties, MouseEvent, ReactElement, ComponentProps } from 'react';

import { Label } from '../FormElements/Label';
import { ReactComponent as CloseIcon } from 'Components/UI_Library/Icons/Close.svg';
import { ReactComponent as LockIcon } from 'Components/UI_Library/Icons/Lock.svg';
import { searchString } from 'Lib/Utils';
import { isDefined } from 'utils/typeguards/isDefined';
import TextInput from '../FormElements/TextInput';
import ToggleIndicator from '../Icons/ToggleIndicator';
import Loader from '../LoadingIndicators/Loader';
import LoadingBar from '../LoadingIndicators/LoadingBar';
import { useOnClickOutside } from '../ReusableHooks/useDetectOutsideClick';
import useInfiniteScroll from '../TableElements/Table/useInfiniteScroll';
import classes from './DropDownWithLoader.module.css';
import { calculateMaxHeight } from './utils/calculateMaxHeight';

export type DropDownItem<T = unknown> = T & {
    /**
     * Identifier for the item used use to pass selection to parent. Unless {@link Props.linkSelection} is true, in
     * which case we pass the entire {@link DropDownItem} as what's selected... Which we do as a second argument
     * anyways. Why make it easy when you can make it hard? 🤷
     *
     * It's also what we display if {@link DropDownItem.displayValue} is not provided. This is for simple strings and
     * {@link DropDownItem.displayValue} if you need custom formatting.
     *
     * It's also what we use as React `key` prop unless there's a {@link DropDownItem.id}.
     *
     * // INSPECT: Should we just allow string in {@link DropDownItem.displayValue} and use option as purely the
     * underlying "value", ie identifier hidden from the user? Separate the two concerns?
     */
    option?: string;
    /** React `key` prop. // FIXME: why is it not key? Or used as an actual id as well? */
    id?: string;
    /**
     * Display value.
     *
     * @deprecated Use {@link DropDownItem.option} unless you really need to provide jsx and not just a string.
     */
    displayValue?: ReactElement;
    /** The value to matched when searching */
    searchValue?: string;
    icon?: ReactElement;
    /** I have no clue why this is here? */
    email?: string;
    value?: string;
    label?: string;
    title?: string;
    onClick?: () => void;
    disabled?: boolean;
};

type Props<T> = {
    /**
     * HTML id attribute for the dropdown.
     *
     * @default randomUUID()
     */
    id?: string;
    makeSelection: (_selection: undefined | string | DropDownItem<T>, _fullDetails: DropDownItem<T>) => void;
    items: readonly DropDownItem<T>[];
    /**
     * // NOTE: This is not an identifier for the what is selected, but the display value for what is selected.
     */
    selected?: null | string | ReactElement;
    disabled?: boolean;
    dropDownOnTop?: boolean;
    dropdownWidth?: number | string;
    dropdownHorizontalShift?: number;
    dropdownVerticalShift?: number;
    overrideSelection?: boolean;
    dark?: boolean;
    adjustHeight?: boolean;
    ghost?: boolean;
    required?: boolean;
    grey?: boolean;
    error?: boolean;
    errorMessage?: string;
    minimise?: boolean;
    search?: boolean;
    searchPlaceholder?: string;
    marginTop?: string | number;
    marginBottom?: string | number;
    placeholder?: string | ReactElement;
    loading?: boolean;
    style?: CSSProperties;
    titleStyle?: Record<string, unknown>;
    containerStyle?: Record<string, unknown>;
    placeholderStyle?: Record<string, unknown>;
    labelStyles?: Record<string, unknown>;
    iconFill?: boolean;
    linkSelection?: boolean;
    loadMore?: () => Promise<void>;
    nextToken?: string | boolean;
    infiniteScroll?: boolean;
    renderInfiniteScroll?: boolean;
    testId?: string;
    clearSelectionCallback?: () => void;
    /** small text underneath the selected text */
    subText?: string;
    subTextLink?: (e: MouseEvent) => void;
} & Partial<Pick<ComponentProps<typeof Label>, 'label' | 'labelRight'>>;

const DropDown = <T,>({ id: idAttribute = v4(), ...props }: Props<T>) => {
    const selectBox = useRef<HTMLDivElement>(null);
    const dropdownRef = useRef<HTMLDivElement>(null);
    const [selected, setSelected] = useState<null | string | ReactElement>(props.selected || null);
    const [isActive, setIsActive] = useState(false);
    const [search, setSearch] = useState('');
    const [itemsPosition, setItemsPosition] = useState({});

    useOnClickOutside(dropdownRef, selectBox, () => closeDropdown());

    const { loadMoreData, loaderRef } = useInfiniteScroll({ loadMore: props.loadMore, nextToken: props.nextToken });

    const toggle = (
        e: MouseEvent<HTMLLabelElement, globalThis.MouseEvent> | MouseEvent<HTMLDivElement, globalThis.MouseEvent>
    ) => {
        if (props.disabled) return;

        e.stopPropagation();
        if (!isActive) {
            setIsActive(true);
            document.querySelector('html')?.classList.add('dropdown-open');
        } else {
            closeDropdown();
        }
    };

    useEffect(() => {
        if (props.selected) setSelected(props.selected);
        else setSelected(null);
    }, [props.selected]);

    useEffect(
        () => () => {
            closeDropdown();
        },
        []
    );

    const closeDropdown = () => {
        setIsActive(false);
        document.querySelector('html')?.classList.remove('dropdown-open');
    };

    const doSelect = (
        item: undefined | string | DropDownItem<T>,
        e: MouseEvent<HTMLLabelElement, globalThis.MouseEvent> | MouseEvent<HTMLDivElement, globalThis.MouseEvent>,
        fullDetails: DropDownItem<T>
    ) => {
        e.stopPropagation();
        props.makeSelection(item, fullDetails);
        !props.overrideSelection && setSelected(typeof item === 'string' ? item : item?.option ?? null);
        closeDropdown();
        setSearch('');
    };

    const searchFilter = (item: DropDownItem<T>) => {
        if (item.searchValue) return searchString(item.searchValue, search);
        if (isDefined(item.option) && typeof item.option === 'string') return searchString(item.option, search);
        // If option is invalid for any reason (normally legacy jsx), return true to prevent the list being blank
        return true;
    };

    const getParentPosition = () => {
        const parentPosition = selectBox?.current?.getBoundingClientRect();
        if (parentPosition) {
            const { left, bottom, top, width } = parentPosition;
            const dropdownWidth = props.dropdownWidth ? props.dropdownWidth : width;
            const maxHeight = calculateMaxHeight(window.innerHeight, bottom);

            if (props?.dropDownOnTop) {
                setItemsPosition({
                    left: left + (props?.dropdownHorizontalShift || 0),
                    top: top - 180 + (props?.dropdownVerticalShift || 0),
                    width: dropdownWidth,
                    minWidth: dropdownWidth,
                    maxHeight,
                });
            } else {
                setItemsPosition({
                    left: left + (props?.dropdownHorizontalShift || 0),
                    top: bottom + (props?.dropdownVerticalShift || 0),
                    width: dropdownWidth,
                    minWidth: dropdownWidth,
                    maxHeight,
                });
            }
        }
    };

    // listen for various events to update the position of the item dropdown
    useEffect(() => {
        if (isActive) {
            getParentPosition();
            window.addEventListener('wheel', () => getParentPosition());
            window.addEventListener('resize', () => getParentPosition());
        }

        return () => {
            window.removeEventListener('wheel', () => getParentPosition());
            window.removeEventListener('resize', () => getParentPosition());
        };
    }, [isActive]);

    const getDropdownClasses = () => {
        let dropdownClasses = `${classes.select}`;
        if (props.dark) dropdownClasses += ` ${classes.dark}`;
        if (isActive && props.adjustHeight) dropdownClasses += ` ${classes.adjustHeight}`;
        if (props.disabled) dropdownClasses += ` ${classes.disabled}`;
        if (props.ghost) dropdownClasses += ` ${classes.ghost}`;
        if (props.grey) dropdownClasses += ` ${classes.grey}`;
        if (props.error) dropdownClasses += ` ${classes.error}`;
        if (props.minimise) dropdownClasses += ` ${classes.minimise}`;
        return dropdownClasses;
    };

    const mobileClose = (extraClass?: 'select' | 'noSearch') => (
        <div className={`${classes.mobileDropdown} ${extraClass ? classes?.[extraClass] : ''}`} onClick={closeDropdown}>
            <span>
                <CloseIcon style={{ stroke: '#0B1A3F' }} />
            </span>
        </div>
    );

    const handleClear = () => {
        closeDropdown();
        props?.clearSelectionCallback?.();
    };

    return (
        <>
            {props.label && (
                <Label
                    className={classes.label}
                    style={{ marginTop: props.marginTop || '', ...props.labelStyles }}
                    htmlFor={idAttribute}
                    label={props.label}
                    labelRight={props.labelRight}
                    required={props.required}
                />
            )}
            <div
                className={getDropdownClasses()}
                ref={selectBox}
                style={{ marginBottom: props.marginBottom ? props.marginBottom : '', ...props.style }}
                onMouseOver={() => getParentPosition()}
            >
                {props.loading && (
                    <div className={classes.titleBox}>
                        <div className={classes.title}>
                            {selected && <span>{selected}</span>}
                            <Loader type="in-button blue" />
                        </div>
                    </div>
                )}
                {!props.loading && (
                    <div
                        id={idAttribute}
                        className={classes.titleBox}
                        onClick={toggle}
                        title={selected && typeof selected === 'string' ? selected : ''}
                        style={{ ...props.containerStyle }}
                        data-testid={props.testId}
                    >
                        <div
                            className={classes.title}
                            style={{ cursor: props.disabled ? 'default' : 'pointer', ...props.titleStyle }}
                        >
                            {props.subText ? (
                                <>
                                    <span style={{ ...props.placeholderStyle, marginBottom: '10px' }}>
                                        {selected || props.placeholder}
                                    </span>
                                    <p
                                        style={{ position: 'absolute', top: '18px', color: '#457ced' }}
                                        onClick={(e) => {
                                            props.subTextLink?.(e);
                                        }}
                                    >
                                        <small> {props.subText} </small>
                                    </p>
                                </>
                            ) : (
                                <span style={{ ...props.placeholderStyle }}>{selected || props.placeholder}</span>
                            )}

                            {!props.disabled && !props.ghost && (
                                <ToggleIndicator dark={props.dark} toggled={isActive} />
                            )}
                            {props.disabled && (
                                <div className={classes.lock}>
                                    <LockIcon />
                                </div>
                            )}
                        </div>
                    </div>
                )}

                {isActive &&
                    !props.disabled &&
                    ReactDOM.createPortal(
                        <div
                            className={`${classes.searchArea} ${props.dark ? 'blue-scrollbar' : ''}`}
                            style={itemsPosition}
                            ref={dropdownRef}
                            role="portal"
                        >
                            {props.clearSelectionCallback ? (
                                <div className={`${classes.clear}`} onClick={handleClear}>
                                    Clear Selection
                                </div>
                            ) : null}

                            {props.search ? (
                                <div className={classes.searchInput}>
                                    <TextInput
                                        small
                                        value={search}
                                        onChange={(e: { target: { value: string } }) => setSearch(e.target.value)}
                                        placeholder={props.searchPlaceholder || ''}
                                        autoFocus
                                    />
                                    {mobileClose()}
                                </div>
                            ) : (
                                mobileClose('noSearch')
                            )}

                            <div className={classes.resultArea}>
                                {props.items.filter(searchFilter).map((item) => (
                                    <div
                                        key={item?.id || item.option}
                                        className={`${classes.listItem} ${props.iconFill ? classes.iconFill : ''}`}
                                        onClick={(e) =>
                                            !item.disabled &&
                                            doSelect(props.linkSelection ? item : item?.option, e, item)
                                        }
                                        title={item?.email || item?.title}
                                        style={{
                                            ...(item.disabled
                                                ? {
                                                      opacity: 0.6,
                                                      cursor: 'not-allowed',
                                                  }
                                                : {}),
                                        }}
                                    >
                                        {item.icon}
                                        {item.displayValue || item.option}
                                    </div>
                                ))}
                                {props.infiniteScroll && props.renderInfiniteScroll && (
                                    <div className={classes.infiniteScroll} ref={loaderRef}>
                                        <span style={{ display: loadMoreData ? 'block' : 'none' }}>
                                            <LoadingBar />
                                        </span>
                                    </div>
                                )}
                            </div>
                        </div>,
                        document.body
                    )}
            </div>
            {props.errorMessage && <span className={classes.errorMessage}>{props.errorMessage}</span>}
        </>
    );
};

/* eslint-enable jsx-a11y/aria-role -- Should add these for accessibility but don't want to change this file to much */
/* eslint-enable jsx-a11y/no-noninteractive-element-interactions -- Should add these for accessibility but don't want to change this file to much */
/* eslint-enable jsx-a11y/mouse-events-have-key-events -- Should add these for accessibility but don't want to change this file to much */

export default DropDown;
